import type { FuzzyMatchRange } from "./types";

import { buildNormalizationIndexMap } from "./normalize";

export type HighlightTextFragment = {
    readonly text: string;
    readonly highlighted: boolean;
};

const mergeNormRanges = (ranges: readonly FuzzyMatchRange[]): readonly FuzzyMatchRange[] => {
    if (ranges.length === 0) {
        return [];
    }

    const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
    const merged: [number, number][] = [[sorted[0][0], sorted[0][1]]];

    for (let index = 1; index < sorted.length; index++) {
        const current = sorted[index];
        const last = merged[merged.length - 1];

        if (current[0] <= last[1] + 1) {
            last[1] = Math.max(last[1], current[1]);
        } else {
            merged.push([current[0], current[1]]);
        }
    }

    return merged;
};

const mapNormRangeToDisplay = (
    displayText: string,
    normRange: FuzzyMatchRange,
    normIndexToOrigStart: readonly number[],
): FuzzyMatchRange | null => {
    const [normStart, normEnd] = normRange;

    if (normStart >= normIndexToOrigStart.length) {
        return null;
    }

    const displayStart = normIndexToOrigStart[normStart];
    const displayEnd =
        normEnd + 1 < normIndexToOrigStart.length ? normIndexToOrigStart[normEnd + 1] - 1 : displayText.length - 1;

    if (displayStart > displayEnd) {
        return null;
    }

    return [displayStart, displayEnd];
};

/**
 * Splits a display label into fragments, highlighting inclusive character ranges that Fuse reported on
 * the same normalised form {@link buildNormalizationIndexMap} produces for search.
 */
export const segmentDisplayByNormalizedRanges = (
    displayText: string,
    ranges: readonly FuzzyMatchRange[],
    locale: string,
): readonly HighlightTextFragment[] => {
    if (!ranges.length) {
        return displayText === "" ? [] : [{ text: displayText, highlighted: false }];
    }

    const { normIndexToOrigStart } = buildNormalizationIndexMap(displayText, locale);
    const displayRanges = mergeNormRanges(ranges)
        .map((range) => mapNormRangeToDisplay(displayText, range, normIndexToOrigStart))
        .filter((range): range is FuzzyMatchRange => range !== null);

    if (!displayRanges.length) {
        return [{ text: displayText, highlighted: false }];
    }

    const fragments: HighlightTextFragment[] = [];
    let cursor = 0;

    for (const [start, end] of displayRanges) {
        if (cursor < start) {
            fragments.push({ text: displayText.slice(cursor, start), highlighted: false });
        }

        fragments.push({ text: displayText.slice(start, end + 1), highlighted: true });
        cursor = end + 1;
    }

    if (cursor < displayText.length) {
        fragments.push({ text: displayText.slice(cursor), highlighted: false });
    }

    return fragments;
};

/** Fuse fuzzy matches scatter single-character indices across the label; the longest span is the real hit. */
export const pickLongestNormalizedRange = (ranges: readonly FuzzyMatchRange[]): FuzzyMatchRange | undefined => {
    const merged = mergeNormRanges(ranges);

    if (!merged.length) {
        return undefined;
    }

    return merged.reduce((longest, range) => (range[1] - range[0] > longest[1] - longest[0] ? range : longest));
};

/**
 * Highlights what the user typed when it appears in the label; otherwise paints Fuse's longest contiguous
 * match span. Avoids per-letter boxes from Fuse's scattered fuzzy `includeMatches` indices.
 */
export const segmentDisplayForSearchHighlight = (
    displayText: string,
    searchTerm: string,
    fuseRanges: readonly FuzzyMatchRange[] | undefined,
    locale: string,
    segmentStringBySearchTerm: (text: string, term: string) => readonly HighlightTextFragment[],
): readonly HighlightTextFragment[] => {
    const query = searchTerm.trim();

    if (!query) {
        return displayText === "" ? [] : [{ text: displayText, highlighted: false }];
    }

    const substringFragments = segmentStringBySearchTerm(displayText, query);

    if (substringFragments.some((fragment) => fragment.highlighted)) {
        return substringFragments;
    }

    const longestRange = fuseRanges ? pickLongestNormalizedRange(fuseRanges) : undefined;

    if (longestRange) {
        return segmentDisplayByNormalizedRanges(displayText, [longestRange], locale);
    }

    return [{ text: displayText, highlighted: false }];
};
